// ZIP reading from a browser File, without loading the archive into memory.
//
// A 3.5 GB export cannot be `await file.arrayBuffer()`d — that kills the tab.
// Everything here works through File.slice(), so only the central directory
// and one member are resident at a time.

const EOCD = 0x06054b50, CEN = 0x02014b50, LOC = 0x04034b50;
const EOCD64_LOCATOR = 0x07064b50, EOCD64 = 0x06064b50;

/**
 * O nome de uma entrada, apesar do que a flag diz.
 *
 * A norma reserva o bit 0x800 para "este nome está em UTF-8" e manda usar
 * CP437 quando ele está desligado. O WhatsApp escreve UTF-8 e não liga o
 * bit — medido num export real: os PDFs de uma conversa vinham como
 * `Orc╠ºamento Gle╠énio…` depois de passarem por um decodificador de byte
 * único, e nenhum deles era encontrado.
 *
 * Isto não se via nas fotos porque `00000042-PHOTO-2025-09-23.png` é ASCII,
 * onde os dois decodificadores concordam. Só quebrava em nomes com acentos,
 * e quem os tem são os ficheiros que uma pessoa nomeou.
 *
 * Então: tentar UTF-8 sempre, em modo estrito, e cair para latin1 só quando
 * os bytes não forem UTF-8 válido — que é o que um nome verdadeiramente
 * CP437 faz. É o que os leitores de zip do mundo real fazem, pela mesma
 * razão.
 */
export function decodeName(bytes: Uint8Array, flags: number): string {
  if (!(flags & 0x800)) {
    try { return new TextDecoder('utf-8', { fatal: true }).decode(bytes); }
    catch { return new TextDecoder('latin1').decode(bytes); }
  }
  return new TextDecoder('utf-8').decode(bytes);
}

export interface ZipEntry {
  name: string; method: number;
  compressed: number; uncompressed: number; localOffset: number;
}

const dv = (b: Uint8Array) => new DataView(b.buffer, b.byteOffset, b.byteLength);

async function slice(file: Blob, start: number, end: number) {
  return new Uint8Array(await file.slice(start, end).arrayBuffer());
}

export async function openZip(file: Blob) {
  const tailLen = Math.min(66_000, file.size);
  const tail = await slice(file, file.size - tailLen, file.size);
  const tv = dv(tail);

  let eocd = -1;
  for (let i = tail.length - 22; i >= 0; i--) {
    if (tv.getUint32(i, true) === EOCD) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('This does not look like a WhatsApp export (.zip).');

  let count = tv.getUint16(eocd + 10, true);
  let cdOffset = tv.getUint32(eocd + 16, true);

  if (cdOffset === 0xffffffff || count === 0xffff) {
    const loc = eocd - 20;
    if (loc < 0 || tv.getUint32(loc, true) !== EOCD64_LOCATOR) throw new Error('zip64 locator missing');
    const z64at = Number(tv.getBigUint64(loc + 8, true));
    const z64 = await slice(file, z64at, z64at + 56);
    const zv = dv(z64);
    if (zv.getUint32(0, true) !== EOCD64) throw new Error('zip64 record missing');
    count = Number(zv.getBigUint64(32, true));
    cdOffset = Number(zv.getBigUint64(48, true));
  }

  const cd = await slice(file, cdOffset, file.size - tailLen + eocd);
  const cv = dv(cd);
  const entries: ZipEntry[] = [];
  let p = 0;
  // Ver decodeName.

  for (let i = 0; i < count && p + 46 <= cd.length; i++) {
    if (cv.getUint32(p, true) !== CEN) break;
    const flags = cv.getUint16(p + 8, true);
    const method = cv.getUint16(p + 10, true);
    let compressed = cv.getUint32(p + 20, true);
    let uncompressed = cv.getUint32(p + 24, true);
    const nameLen = cv.getUint16(p + 28, true);
    const extraLen = cv.getUint16(p + 30, true);
    const commentLen = cv.getUint16(p + 32, true);
    let localOffset = cv.getUint32(p + 42, true);
    const nameBytes = cd.subarray(p + 46, p + 46 + nameLen);
    const name = decodeName(nameBytes, flags);

    if (compressed === 0xffffffff || uncompressed === 0xffffffff || localOffset === 0xffffffff) {
      let e = p + 46 + nameLen;
      const end = e + extraLen;
      while (e + 4 <= end) {
        const id = cv.getUint16(e, true), size = cv.getUint16(e + 2, true);
        if (id === 0x0001) {
          let q = e + 4;
          if (uncompressed === 0xffffffff) { uncompressed = Number(cv.getBigUint64(q, true)); q += 8; }
          if (compressed === 0xffffffff) { compressed = Number(cv.getBigUint64(q, true)); q += 8; }
          if (localOffset === 0xffffffff) localOffset = Number(cv.getBigUint64(q, true));
          break;
        }
        e += 4 + size;
      }
    }

    if (!name.endsWith('/')) entries.push({ name, method, compressed, uncompressed, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }

  /** Reads and inflates one member. Only this member is resident. */
  async function read(entry: ZipEntry): Promise<Uint8Array> {
    const head = await slice(file, entry.localOffset, entry.localOffset + 30);
    const hv = dv(head);
    if (hv.getUint32(0, true) !== LOC) throw new Error(`corrupt local header for ${entry.name}`);
    const start = entry.localOffset + 30 + hv.getUint16(26, true) + hv.getUint16(28, true);
    const raw = file.slice(start, start + entry.compressed);
    if (entry.method === 0) return new Uint8Array(await raw.arrayBuffer());
    if (entry.method !== 8) throw new Error(`unsupported compression in ${entry.name}`);
    const stream = raw.stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  return { entries, read };
}
